/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useContext } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, Card, Tab, Tabs, Tooltip, Typography } from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import NotesIcon from "@mui/icons-material/Notes";
import HistoryIcon from "@mui/icons-material/History";
import FolderIcon from "@mui/icons-material/Folder";
import { ClientMessageModel } from "../../Models/Models";
import { CacheContext } from "../../Context/cacheContext";
import { Vocabulary } from "../../Utils/Vocabulary";
import { urlEnum, localUrlEnum } from "../../Utils/urlEnum";
import { getData } from "../../Services/getData";
import { isMobile } from "../../Utils/utils";
import { ClientMessageSource, RolesEnum } from "../../Utils/Constants";
import LeadActivity from "./LeadDetail/LeadActivity";
import LeadDocuments from "./LeadDetail/LeadDocuments";
import ClientVehiclesPanel from "../Clients/ClientVehiclesPanel";
import LeadHeader from "./LeadHeader";
import LeadStatusPipeline from "./LeadDetail/LeadStatusPipeline";
import Client from "../Clients/Client";
import { ClientData } from "../Clients/Clients";
import HistoryComponent from "../HistoryComponent";
import LeftSidebar from "./LeftSidebar";
import RightSidebar from "./Components/RightSidebar";
import styles from "./LeadDetail.module.css";

function TabPanel({ children, value, index }: { children?: React.ReactNode; value: number; index: number }) {
  return (
    <div hidden={value !== index} role="tabpanel">
      {value === index && children}
    </div>
  );
}

export default function LeadDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const cacheContext = useContext(CacheContext);
  const initialLead: ClientMessageModel | undefined = location.state?.lead;

  const [lead, setLead] = useState<ClientMessageModel>(initialLead || new ClientMessageModel());
  const [tab, setTab] = useState(0);
  const [statusId, setStatusId] = useState<number | undefined>(initialLead?.status_id);
  const [assignedUserId, setAssignedUserId] = useState<number | null | undefined>(initialLead?.first_seen_user_id);
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [pendingFollowUps, setPendingFollowUps] = useState<any[]>([]);
const isAdmin = (window.localStorage.getItem("roles") || "").includes(RolesEnum.admin);
  const isLeadUri = lead.source === ClientMessageSource.leadUri;

  function buildClientData(): ClientData {
    const client = lead.client;
    const nameParts = (lead.name || "").trim().split(" ");
    const lastName = nameParts[0] || "";
    const firstName = nameParts.slice(1).join(" ") || "";
    return {
      id: client?.id ?? null,
      companyName: client?.companyName || client?.company_name || "",
      lastName: client?.lastName || client?.last_name || lastName,
      firstName: client?.firstName || client?.first_name || firstName,
      email: client?.email || lead.email || "",
      phone: client?.phone || lead.phone || "",
      CNP: client?.CNP || client?.cnp || "",
      series: client?.series || "",
      number: client?.number || "",
      CUI: client?.CUI || client?.cui || lead.additional_params?.cui_companie || "",
      nrRegCom: client?.nrRegCom || client?.nr_reg_com || "",
      street: client?.street || "",
      city: client?.city || "",
      country: client?.country || "Romania",
      isLegalPerson: client?.isLegalPerson ?? client?.is_legal_person ?? false,
      createdByName: client?.createdByName || "",
    };
  }

  function refetchLead() {
    const leadId = id || initialLead?.id;
    if (!leadId) return;
    getData(`${urlEnum.clientMessages}/${leadId}`)
      .then((res: any) => {
        const fetched = res?.data?.message;
        if (!fetched) return;
        setLead(fetched);
        setStatusId(fetched.status_id);
        setAssignedUserId(fetched.first_seen_user_id);
      })
      .catch(() => { /* silently ignore */ });
  }

  useEffect(() => {
    refetchLead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!lead.id) return;
    getData(`${urlEnum.leadFollowUps}/${lead.id}`).catch(() => { /* silently ignore */ });
  }, [lead.id]);

  if (!initialLead && !id) {
    return (
      <div className={styles.emptyState}>
        <Typography variant="h6" color="text.secondary">{Vocabulary.noData}</Typography>
        <Button style={{ marginTop: 16 }} variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(localUrlEnum.clientMessages)}>
          {Vocabulary.backToLeads}
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.stickyHeader}>
        <LeadHeader lead={lead} pendingFollowUps={pendingFollowUps} onConvert={() => setConvertModalOpen(true)} />

        <LeadStatusPipeline
          statuses={cacheContext.cache.clientMessagesStatus || []}
          activeStatusId={statusId}
          leadId={lead.id}
          assignedUserId={assignedUserId}
          onStatusChanged={(newStatusId: number, cancelReason: string | null) => {
            setStatusId(newStatusId);
            setLead({ ...lead, cancel_reason: cancelReason });
          }}
        />
      </div>

      <div className={styles.body}>
        {/* LEFT */}
        <div className={styles.leftScrollWrapper}>
          <LeftSidebar lead={lead} isLeadUri={isLeadUri} />
        </div>

        {/* CENTER */}
        <div className={styles.centerPanel}>
          <Card elevation={0} className={styles.activityCard}>
            <div className={styles.tabHeader}>
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                style={{ padding: "0 8px", minHeight: 44 }}
                TabIndicatorProps={{ style: { height: 3, borderRadius: "3px 3px 0 0", background: "#FF6B1A" } }}
              >
                <Tooltip title={isMobile() ? Vocabulary.notes : ""} placement="bottom">
                  <Tab icon={<NotesIcon style={{ fontSize: 15 }} />} iconPosition="start" label={isMobile() ? undefined : Vocabulary.notes} className={styles.tab} />
                </Tooltip>
                <Tooltip title={isMobile() ? Vocabulary.history : ""} placement="bottom">
                  <Tab icon={<HistoryIcon style={{ fontSize: 15 }} />} iconPosition="start" label={isMobile() ? undefined : Vocabulary.history} className={styles.tab} />
                </Tooltip>
                {lead.client && (
                  <Tooltip title={isMobile() ? (Vocabulary.documents || "Documente") : ""} placement="bottom">
                    <Tab icon={<FolderIcon style={{ fontSize: 15 }} />} iconPosition="start" label={isMobile() ? undefined : (Vocabulary.documents || "Documente")} className={styles.tab} />
                  </Tooltip>
                )}
                {lead.client && (
                  <Tooltip title={isMobile() ? Vocabulary.vehicles : ""} placement="bottom">
                    <Tab icon={<DirectionsCarIcon style={{ fontSize: 15 }} />} iconPosition="start" label={isMobile() ? undefined : Vocabulary.vehicles} className={styles.tab} />
                  </Tooltip>
                )}
              </Tabs>
            </div>
            <div className={styles.contentScroll}>
              <TabPanel value={tab} index={0}>
                <LeadActivity leadId={lead.id} onPendingFollowUpsChange={setPendingFollowUps} />
              </TabPanel>
              <TabPanel value={tab} index={1}>
                {lead.logs?.length ? (
                  <HistoryComponent
                    logs={lead.logs}
                    firstAllocMessage={Vocabulary.clientMessageWasAllocated}
                    firstTimeStatusSetMessage={Vocabulary.clientMessageSetStatus}
                    statusChangedMessage={Vocabulary.clientMessageStatusChanged}
                    type={Vocabulary.message}
                    createdMessage={Vocabulary.clientMessageCreated}
                  />
                ) : (
                  <div className={styles.noHistory}>
                    <HistoryIcon style={{ fontSize: 40, color: "#d1d5db", marginBottom: 8 }} />
                    <Typography variant="body2" color="text.disabled" style={{ fontStyle: "italic" }}>
                      {Vocabulary.noHistory || "Nu există istoric disponibil."}
                    </Typography>
                  </div>
                )}
              </TabPanel>
              {lead.client && (
                <TabPanel value={tab} index={2}>
                  <LeadDocuments clientId={lead.client.id} />
                </TabPanel>
              )}
              {lead.client && (
                <TabPanel value={tab} index={3}>
                  <ClientVehiclesPanel clientId={lead.client.id} />
                </TabPanel>
              )}
            </div>
          </Card>
        </div>

        {/* RIGHT */}
        <div className={styles.rightSidebarWrapper}>
          <RightSidebar
            lead={lead}
            isAdmin={isAdmin}
            statusId={statusId}
            assignedUserId={assignedUserId}
            setAssignedUserId={setAssignedUserId}
            onConvert={() => setConvertModalOpen(true)}
            refetchLead={refetchLead}
          />
        </div>
      </div>


      <Client
        open={convertModalOpen}
        title={lead.client ? Vocabulary.updateClient || "Actualizează client" : Vocabulary.convertLeadToClient}
        editedClient={convertModalOpen ? buildClientData() : null}
        sourceLeadId={lead.id}
        onClose={(converted) => {
          setConvertModalOpen(false);
          if (converted) refetchLead();
        }}
      />
    </div>
  );
}
